import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import DateTimePicker from '../components/DateTimePicker';
import {
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  DocumentIcon,
  FolderIcon,
  EyeIcon,
  ArrowPathIcon,
  PencilIcon,
  ClipboardDocumentIcon,
  CameraIcon,
  PrinterIcon,
  FolderPlusIcon,
  DocumentDuplicateIcon,
  PlusCircleIcon,
  ArrowRightCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  UserIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { usePageTitle } from '../contexts/PageTitleContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../contexts/AuthContext';
import { apiService, Policy, FilePermissions, FolderPermissions as ApiFolderPermissions, RecipientTarget } from '../services/api';
import PolicyRecipientSelector from '../components/PolicyRecipientSelector';
import TagManager from '../components/TagManager';
import { useCompanyPolicySettings } from '../hooks/useCompanyPolicySettings';

const PolicyEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useUser();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { setPageTitle } = usePageTitle();
  const { setHasUnsavedChanges, setOnSave, setIsSubmitting, isSubmitting } = useUnsavedChanges();
  const { isFilePermissionVisible, isFolderPermissionVisible } = useCompanyPolicySettings();

  const [newDomain, setNewDomain] = useState('');
  const [newSubdomain, setNewSubdomain] = useState('');
  const [accessLimitationsEnabled, setAccessLimitationsEnabled] = useState(false);
  const [accessCountEnabled, setAccessCountEnabled] = useState(false);
  const [timeLimitationType, setTimeLimitationType] = useState<'days' | 'datetime'>('days');
  const [daysValue, setDaysValue] = useState<number | ''>('');
  const [daysUnit, setDaysUnit] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const [datetimeErrors, setDatetimeErrors] = useState<{
    from?: string;
    to?: string;
  }>({});
  const [datetimeFrom, setDatetimeFrom] = useState<string>('');
  const [datetimeTo, setDatetimeTo] = useState<string>('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showAddRecipientsModal, setShowAddRecipientsModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recipientToDelete, setRecipientToDelete] = useState<string | null>(null);
  const [isEditingRecipientMessage, setIsEditingRecipientMessage] = useState(false);
  const [recipientMessage, setRecipientMessage] = useState<string>('');

  // Fetch policy data
  const { data: policyData, isLoading: policyLoading, error: policyError } = useQuery({
    queryKey: ['policy', id],
    queryFn: () => apiService.getPolicyById(id!),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  });

  const isLoading = policyLoading;
  const error = policyError;

  // Fetch policy recipients
  const { data: policyRecipients, isLoading: recipientsLoading } = useQuery({
    queryKey: ['policyRecipients', id],
    queryFn: () => apiService.getPolicyRecipients(id!),
    enabled: !!id,
  });

  // Fetch user groups for display
  const { data: userGroupsResponse } = useQuery({
    queryKey: ['userGroups'],
    queryFn: () => apiService.getUserGroups(),
  });

  // Fetch contacts for member details
  const { data: contacts } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => apiService.getContacts(),
  });

  // Fetch group members for expanded groups
  const expandedGroupIds = Array.from(expandedGroups);
  const { data: groupMembersData } = useQuery({
    queryKey: ['groupMembers', expandedGroupIds],
    queryFn: async () => {
      if (expandedGroupIds.length === 0) return {};
      const membersData: Record<string, any[]> = {};
      for (const groupId of expandedGroupIds) {
        try {
          const members = await apiService.getUserGroupMembers(groupId);
          membersData[groupId] = members;
        } catch (error) {
          console.error(`Failed to fetch members for group ${groupId}:`, error);
          membersData[groupId] = [];
        }
      }
      return membersData;
    },
    enabled: expandedGroupIds.length > 0,
  });

  // Restrict edit page: admin cannot edit system policies; user cannot edit system or company policies
  useEffect(() => {
    if (!policyData || !user || !id) return;
    const isViewOnly =
      (user.role === 'admin' && policyData.type === 'system') ||
      (user.role === 'user' && (policyData.type === 'system' || policyData.type === 'company'));
    if (isViewOnly) {
      navigate(`/policies/${id}`, { replace: true });
    }
  }, [policyData, user, id, navigate]);

  // Create policy state for editing
  const [editingPolicy, setEditingPolicy] = useState<Policy>({
    id: id || 'mock-policy-id',
    name: 'Default Policy',
    description: '',
    type: 'user',
    asset_type: 'file',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: profile?.id || '',
    updated_by: profile?.id || '',
    settings: {
      encryption_type: 'AES-256',
      recipient_settings: {
        allowed_domains: [],
        allowed_subdomains: [],
        require_approval: false,
      },
      file_permissions: {
        convert_original: { allowed: false, audit_level: 'basic' },
        save_changes: { allowed: false, audit_level: 'basic' },
        copy_paste: { allowed: false, audit_level: 'basic' },
        screen_capture: { allowed: false, audit_level: 'basic' },
        print: { allowed: false, audit_level: 'basic' },
        share: { allowed: false, audit_level: 'basic' },
      },
      folder_permissions: {
        create: { allowed: false, audit_level: 'basic' },
        copy: { allowed: false, audit_level: 'basic' },
        add: { allowed: false, audit_level: 'basic' },
        move: { allowed: false, audit_level: 'basic' },
        share: { allowed: false, audit_level: 'basic' },
        delete: { allowed: false, audit_level: 'basic' },
      },
      screen_watermark: false,
      print_watermark: false,
      security: {
        auth_on_asset_open: false,
        authentication: {
          two_factor: false,
          biometric: false,
          max_attempts: 3,
        },
        session: {
          timeout_seconds: 1800,
          allow_refresh: true,
        },
        protection: {
          delete_on_unauthorized: false,
          delete_on_limit_exceeded: false,
        },
      },
      access_limitations: {
        access_count_limitation: undefined,
        allowed_access_from: undefined,
        allowed_access_to: undefined,
      },
      recipient_message: '',
    },
  });

  const editingPolicyRef = useRef(editingPolicy);

  // Update policy data when policy changes
  React.useEffect(() => {
    if (policyData) {
      console.log('🔄 POLICY EDIT: Updating policy data:', policyData.name);
      console.log('📋 POLICY EDIT: Policy ID:', policyData.id);
      console.log('📋 POLICY EDIT: Asset type:', policyData.asset_type);
      console.log('👥 POLICY EDIT: Recipient settings from backend:', policyData.settings?.recipient_settings);
      console.log('📊 POLICY EDIT: Access limitations from backend:', policyData.settings?.access_limitations);
      
      // Set time-based limitations enabled if there are time values
      const hasTimeLimitations = !!(policyData.settings?.access_limitations && (
        (policyData.settings.access_limitations.allowed_access_from !== null && policyData.settings.access_limitations.allowed_access_from !== undefined) ||
        (policyData.settings.access_limitations.allowed_access_to !== null && policyData.settings.access_limitations.allowed_access_to !== undefined)
      ));
      setAccessLimitationsEnabled(hasTimeLimitations);
      
      // Set access count enabled if there's a count limitation (check if value exists and is > 0)
      const countValue = policyData.settings?.access_limitations?.access_count_limitation;
      const hasCountLimitation = !!(countValue !== null && countValue !== undefined && countValue > 0);
      setAccessCountEnabled(hasCountLimitation);
      
      // Set recipient message
      setRecipientMessage(policyData.settings?.recipient_message || '');
      
      // Set time limitation type based on what's configured
      if (policyData.settings?.access_limitations?.allowed_access_from || policyData.settings?.access_limitations?.allowed_access_to) {
        setTimeLimitationType('datetime');
        // Set datetime values for display
        if (policyData.settings.access_limitations.allowed_access_from) {
          const fromDate = new Date(policyData.settings.access_limitations.allowed_access_from);
          const year = fromDate.getFullYear();
          const month = String(fromDate.getMonth() + 1).padStart(2, '0');
          const day = String(fromDate.getDate()).padStart(2, '0');
          const hours = String(fromDate.getHours()).padStart(2, '0');
          const minutes = String(fromDate.getMinutes()).padStart(2, '0');
          setDatetimeFrom(`${year}-${month}-${day}T${hours}:${minutes}`);
        }
        if (policyData.settings.access_limitations.allowed_access_to) {
          const toDate = new Date(policyData.settings.access_limitations.allowed_access_to);
          const year = toDate.getFullYear();
          const month = String(toDate.getMonth() + 1).padStart(2, '0');
          const day = String(toDate.getDate()).padStart(2, '0');
          const hours = String(toDate.getHours()).padStart(2, '0');
          const minutes = String(toDate.getMinutes()).padStart(2, '0');
          setDatetimeTo(`${year}-${month}-${day}T${hours}:${minutes}`);
        }
      } else {
        setTimeLimitationType('days');
      }
      
      setEditingPolicy({
        id: policyData.id || '',
        name: policyData.name || '',
        description: policyData.description || '',
        type: policyData.type || 'user',
        asset_type: policyData.asset_type || 'file',
        status: policyData.status || 'active',
        created_at: policyData.created_at || new Date().toISOString(),
        updated_at: policyData.updated_at || new Date().toISOString(),
        created_by: policyData.created_by || '',
        updated_by: policyData.updated_by || '',
        settings: {
          encryption_type: policyData.settings?.encryption_type || 'AES-256',
          recipient_settings: policyData.settings?.recipient_settings || {
            allowed_domains: [],
            allowed_subdomains: [],
            require_approval: false,
          },
          file_permissions: policyData.settings?.file_permissions || {
            convert_original: { allowed: false, audit_level: 'basic' },
            save_changes: { allowed: false, audit_level: 'basic' },
            copy_paste: { allowed: false, audit_level: 'basic' },
            screen_capture: { allowed: false, audit_level: 'basic' },
            print: { allowed: false, audit_level: 'basic' },
            share: { allowed: false, audit_level: 'basic' },
          },
          folder_permissions: policyData.settings?.folder_permissions || {
            create: { allowed: false, audit_level: 'basic' },
            copy: { allowed: false, audit_level: 'basic' },
            add: { allowed: false, audit_level: 'basic' },
            move: { allowed: false, audit_level: 'basic' },
            share: { allowed: false, audit_level: 'basic' },
            delete: { allowed: false, audit_level: 'basic' },
          },
          screen_watermark: typeof policyData.settings?.screen_watermark === 'boolean' 
            ? policyData.settings.screen_watermark 
            : (policyData.settings?.screen_watermark as any)?.enabled ?? false,
          print_watermark: typeof policyData.settings?.print_watermark === 'boolean'
            ? policyData.settings.print_watermark
            : (policyData.settings?.print_watermark as any)?.enabled ?? false,
          security: policyData.settings?.security || {
            auth_on_asset_open: false,
            authentication: {
              two_factor: false,
              biometric: false,
              max_attempts: 3,
            },
            session: {
              timeout_seconds: 1800,
              allow_refresh: true,
            },
            protection: {
              delete_on_unauthorized: false,
              delete_on_limit_exceeded: false,
              require_device_trust: false,
            },
          },
          access_limitations: policyData.settings?.access_limitations || {
            access_count_limitation: undefined,
            allowed_access_from: undefined,
            allowed_access_to: undefined,
          },
          recipient_message: policyData.settings?.recipient_message || '',
        },
      });
    }
  }, [policyData]);

  // Set page title when policy data is loaded
  useEffect(() => {
    if (policyData) {
      setPageTitle('Edit Policy', policyData.name);
    }
  }, [policyData, setPageTitle]);

  // Save changes mutation - updating the policy
  const saveChangesMutation = useMutation({
    mutationFn: async (policyData: Policy) => {
      // Transform frontend data structure to match backend expectations
      const backendPolicyData = {
        name: policyData.name,
        description: policyData.description || '',
        type: policyData.type || 'user',
        asset_type: policyData.asset_type || 'file',
        status: policyData.status || 'active',
        settings: {
          encryption_type: policyData.settings.encryption_type,
          recipient_settings: policyData.settings.recipient_settings,
          file_permissions: policyData.settings.file_permissions,
          folder_permissions: policyData.settings.folder_permissions,
          screen_watermark: policyData.settings.screen_watermark,
          print_watermark: policyData.settings.print_watermark,
          security: policyData.settings.security,
          access_limitations: policyData.settings.access_limitations,
          recipient_message: policyData.settings.recipient_message,
        }
      };
      
      console.log('Updating policy with backend structure:', backendPolicyData);
      return apiService.updatePolicy(policyData.id, backendPolicyData as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy', id] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      setHasUnsavedChanges(false);
      setIsSubmitting(false);
      toast.success('Policy updated successfully');
    },
    onError: (error: any) => {
      setIsSubmitting(false);
      toast.error(error.response?.data?.message || 'Failed to update policy');
    },
  });

  const addDomain = () => {
    if (!newDomain.trim()) return;
    
    const trimmedValue = newDomain.trim();
    
    // Check if domain is already in the list
    if (editingPolicy.settings.recipient_settings?.allowed_domains?.includes(trimmedValue)) {
      toast.error('This domain is already in the allowed domains list');
      return;
    }

    // Add to domains list
    setEditingPolicy(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        recipient_settings: {
          allowed_domains: [...(prev.settings.recipient_settings?.allowed_domains || []), trimmedValue],
          allowed_subdomains: prev.settings.recipient_settings?.allowed_subdomains || [],
          require_approval: prev.settings.recipient_settings?.require_approval || false,
        }
      }
    }));
    
    setHasUnsavedChanges(true);
    setNewDomain('');
    toast.success('Domain added successfully');
  };

  const addSubdomain = () => {
    if (!newSubdomain.trim()) return;
    
    const trimmedValue = newSubdomain.trim();
    
    // Check if subdomain is already in the list
    if (editingPolicy.settings.recipient_settings?.allowed_subdomains?.includes(trimmedValue)) {
      toast.error('This subdomain is already in the allowed subdomains list');
      return;
    }

    // Add to subdomains list
    setEditingPolicy(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        recipient_settings: {
          allowed_domains: prev.settings.recipient_settings?.allowed_domains || [],
          allowed_subdomains: [...(prev.settings.recipient_settings?.allowed_subdomains || []), trimmedValue],
          require_approval: prev.settings.recipient_settings?.require_approval || false,
        }
      }
    }));
    
    setHasUnsavedChanges(true);
    setNewSubdomain('');
    toast.success('Subdomain added successfully');
  };

  const removeDomain = (domain: string) => {
    setEditingPolicy(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        recipient_settings: {
          allowed_domains: (prev.settings.recipient_settings?.allowed_domains || []).filter(d => d !== domain),
          allowed_subdomains: prev.settings.recipient_settings?.allowed_subdomains || [],
          require_approval: prev.settings.recipient_settings?.require_approval || false,
        }
      }
    }));
    setHasUnsavedChanges(true);
    toast.success('Domain removed');
  };

  const removeSubdomain = (subdomain: string) => {
    setEditingPolicy(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        recipient_settings: {
          allowed_domains: prev.settings.recipient_settings?.allowed_domains || [],
          allowed_subdomains: (prev.settings.recipient_settings?.allowed_subdomains || []).filter(s => s !== subdomain),
          require_approval: prev.settings.recipient_settings?.require_approval || false,
        }
      }
    }));
    setHasUnsavedChanges(true);
    toast.success('Subdomain removed');
  };

  const updatePermission = (section: string, permission: string, value: boolean | number | string | undefined) => {
    setEditingPolicy(prev => {
      const updated = { ...prev };
      
      if (section === 'file_permissions') {
        if (updated.settings.file_permissions) {
          updated.settings.file_permissions = {
            ...updated.settings.file_permissions,
            [permission]: { 
              allowed: value as boolean, 
              audit_level: updated.settings.file_permissions[permission as keyof FilePermissions]?.audit_level || 'basic' 
            },
          };
        }
      } else if (section === 'folder_permissions') {
        if (updated.settings.folder_permissions) {
          updated.settings.folder_permissions = {
            ...updated.settings.folder_permissions,
            [permission]: { 
              allowed: value as boolean, 
              audit_level: updated.settings.folder_permissions[permission as keyof ApiFolderPermissions]?.audit_level || 'basic' 
            },
          };
        }
      } else if (section === 'security') {
        if (permission === 'auth_on_asset_open') {
          updated.settings.security = {
            ...updated.settings.security,
            auth_on_asset_open: value as boolean,
          };
        } else if (permission === 'two_factor') {
          updated.settings.security.authentication = {
            two_factor: value as boolean,
            biometric: updated.settings.security.authentication?.biometric || false,
            max_attempts: updated.settings.security.authentication?.max_attempts || 3,
          };
        } else if (permission === 'biometric') {
          updated.settings.security.authentication = {
            two_factor: updated.settings.security.authentication?.two_factor || false,
            biometric: value as boolean,
            max_attempts: updated.settings.security.authentication?.max_attempts || 3,
          };
        } else if (permission === 'max_attempts') {
          updated.settings.security.authentication = {
            two_factor: updated.settings.security.authentication?.two_factor || false,
            biometric: updated.settings.security.authentication?.biometric || false,
            max_attempts: value as number,
          };
        } else if (permission === 'timeout_seconds') {
          updated.settings.security.session = {
            timeout_seconds: value as number,
            allow_refresh: updated.settings.security.session?.allow_refresh || true,
          };
        } else if (permission === 'delete_on_unauthorized') {
          updated.settings.security.protection = {
            delete_on_unauthorized: value as boolean,
            delete_on_limit_exceeded: updated.settings.security.protection?.delete_on_limit_exceeded || false,
          };
        } else if (permission === 'delete_on_limit_exceeded') {
          updated.settings.security.protection = {
            delete_on_unauthorized: updated.settings.security.protection?.delete_on_unauthorized || false,
            delete_on_limit_exceeded: value as boolean,
          };
        }
      } else if (section === 'watermark') {
        if (permission === 'screen_enabled') {
          updated.settings.screen_watermark = value as boolean;
        } else if (permission === 'print_enabled') {
          updated.settings.print_watermark = value as boolean;
        }
      } else if (section === 'general') {
        if (permission === 'access_count_limitation') {
          updated.settings.access_limitations = {
            ...updated.settings.access_limitations,
            access_count_limitation: value !== undefined ? (value as number) : undefined,
          };
        } else if (permission === 'allowed_access_from') {
          updated.settings.access_limitations = {
            ...updated.settings.access_limitations,
            allowed_access_from: value as string,
          };
        } else if (permission === 'allowed_access_to') {
          updated.settings.access_limitations = {
            ...updated.settings.access_limitations,
            allowed_access_to: value as string,
          };
        }
      }
      
      return updated;
    });
    
    setHasUnsavedChanges(true);
  };

  const handleSaveChangesRef = useRef<(() => void) | undefined>(undefined);
  
  const handleSaveChanges = useCallback(() => {
    saveChangesMutation.mutate(editingPolicyRef.current);
  }, [saveChangesMutation]);

  // Draft-specific save functions
  const handleKeepAsDraft = useCallback(() => {
    const draftPolicy = {
      ...editingPolicyRef.current,
      status: 'draft' as const
    };
    saveChangesMutation.mutate(draftPolicy);
  }, [saveChangesMutation]);

  const handlePublish = useCallback(() => {
    const publishedPolicy = {
      ...editingPolicyRef.current,
      status: 'active' as const
    };
    saveChangesMutation.mutate(publishedPolicy);
  }, [saveChangesMutation]);

  // Update refs when dependencies change
  useEffect(() => {
    editingPolicyRef.current = editingPolicy;
    handleSaveChangesRef.current = handleSaveChanges;
  }, [editingPolicy, handleSaveChanges]);

  // Set the save function for the header
  useEffect(() => {
    if (policyData?.status === 'draft') {
      // For draft policies, we'll use custom buttons instead of the default save
      setOnSave(null);
    } else {
      // For non-draft policies, use the default save behavior
      setOnSave(() => () => handleSaveChangesRef.current?.());
    }
    return () => setOnSave(null);
  }, [setOnSave, policyData?.status]);

  const handleKeyDown = (e: React.KeyboardEvent, type: 'domain' | 'subdomain') => {
    if (e.key === 'Enter') {
      if (type === 'domain') {
        addDomain();
      } else {
        addSubdomain();
      }
    }
  };

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Remove policy recipient mutation
  const removeRecipientMutation = useMutation({
    mutationFn: async (recipientId: string) => {
      return apiService.removePolicyRecipient(id!, recipientId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policyRecipients', id] });
      toast.success('Recipient removed successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to remove recipient');
    },
  });

  const handleRemoveRecipient = (recipientId: string) => {
    setRecipientToDelete(recipientId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteRecipient = () => {
    if (recipientToDelete) {
      removeRecipientMutation.mutate(recipientToDelete);
      setShowDeleteConfirm(false);
      setRecipientToDelete(null);
    }
  };

  const cancelDeleteRecipient = () => {
    setShowDeleteConfirm(false);
    setRecipientToDelete(null);
  };

  // Add policy recipients mutation
  const addRecipientsMutation = useMutation({
    mutationFn: async (targets: RecipientTarget[]) => {
      return apiService.addPolicyRecipients(id!, targets);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policyRecipients', id] });
      setShowAddRecipientsModal(false);
      toast.success('Recipients added successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add recipients');
    },
  });

  const handleAddRecipients = (targets: RecipientTarget[]) => {
    addRecipientsMutation.mutate(targets);
  };

  // Handle time limitation type change and clear values
  const handleTimeLimitationTypeChange = (type: 'days' | 'datetime') => {
    setTimeLimitationType(type);
    // Clear the opposite values when switching
    if (type === 'days') {
      // Clear datetime values when switching to days
      setEditingPolicy(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: undefined,
            allowed_access_to: undefined,
          }
        }
      }));
      setDatetimeFrom('');
      setDatetimeTo('');
    } else {
      // Clear days value when switching to datetime
      setDaysValue('');
      setDaysUnit('day');
      // Also clear the calculated datetime values from days
      setEditingPolicy(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: undefined,
            allowed_access_to: undefined,
          }
        }
      }));
    }
  };

  // Calculate datetime values from days
  const calculateDateTimeFromDays = (days: number, unit: 'day' | 'week' | 'month' | 'year') => {
    const now = new Date();
    let daysToAdd = days;
    
    switch (unit) {
      case 'week':
        daysToAdd = days * 7;
        break;
      case 'month':
        daysToAdd = days * 30; // Approximate
        break;
      case 'year':
        daysToAdd = days * 365; // Approximate
        break;
      default:
        daysToAdd = days;
    }
    
    const fromDate = new Date(now);
    const toDate = new Date(now.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));
    
    return {
      from: fromDate.toISOString(),
      to: toDate.toISOString()
    };
  };

  // Update datetime values when days change
  useEffect(() => {
    if (timeLimitationType === 'days' && accessLimitationsEnabled && daysValue !== '' && daysValue > 0) {
      const { from, to } = calculateDateTimeFromDays(daysValue as number, daysUnit);
      setEditingPolicy(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: from,
            allowed_access_to: to,
          }
        }
      }));
    } else if (timeLimitationType === 'days' && accessLimitationsEnabled && (daysValue === '' || daysValue <= 0)) {
      // If days value is empty or invalid, clear datetime values
      setEditingPolicy(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: undefined,
            allowed_access_to: undefined,
          }
        }
      }));
    }
  }, [daysValue, daysUnit, timeLimitationType, accessLimitationsEnabled]);

  // Validate datetime values (for editing - no past date validation)
  const validateDatetimeValues = (fromValue?: string, toValue?: string) => {
    const errors: { from?: string; to?: string } = {};
    
    // Only validate relative dates (from < to), not past dates during editing
    if (fromValue && toValue) {
      const fromDate = new Date(fromValue);
      const toDate = new Date(toValue);
      if (fromDate >= toDate) {
        errors.to = 'Access to time must be after access from time';
      }
    }
    
    setDatetimeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Toggle Switch Component
  const ToggleSwitch: React.FC<{
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
  }> = ({ checked, onChange, disabled = false }) => (
    <button
      type="button"
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
        checked ? 'bg-green-600' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  if (error || !policyData) {
    return (
      <div className="text-center py-12">
        <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Policy not found</h3>
        <p className="mt-1 text-sm text-gray-500">The policy could not be loaded.</p>
        <button
          onClick={() => navigate('/policies')}
          className="mt-4 btn-primary"
        >
          Back to Policies
        </button>
      </div>
    );
  }

  const { settings } = editingPolicy;

  return (
    <div className="space-y-6">
    

      {/* Draft Policy Actions */}
      {policyData?.status === 'draft' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <DocumentIcon className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Draft Policy</h3>
                <p className="text-xs text-yellow-600">This policy is currently in draft status</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleKeepAsDraft}
                disabled={saveChangesMutation.isPending}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                <DocumentIcon className="w-4 h-4" />
                <span>Keep as Draft</span>
              </button>
              <button
                onClick={handlePublish}
                disabled={saveChangesMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                <ArrowRightCircleIcon className="w-4 h-4" />
                <span>Publish</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Access Limitations */}
          <div className="card">
            <div className="flex items-center space-x-2 mb-4">
              <ShieldCheckIcon className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Access Limitations</h2>
            </div>
            
            <div className="space-y-6">
                {/* Access Count Limitation */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Access Count Limitation
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          const newValue = !accessCountEnabled;
                          setAccessCountEnabled(newValue);
                          
                          // Update policy state
                          if (!newValue) {
                            // If disabling count, remove access_count_limitation
                            setEditingPolicy(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings,
                                access_limitations: {
                                  ...prev.settings.access_limitations,
                                  access_count_limitation: undefined
                                }
                              }
                            }));
                          }
                          setHasUnsavedChanges(true);
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                          accessCountEnabled ? 'bg-governate-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            accessCountEnabled ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  
                  {accessCountEnabled && (
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={settings.access_limitations?.access_count_limitation ?? ''}
                        onChange={(e) => {
                          if (e.target.value === '') {
                            // Empty input means unlimited - set to undefined
                            updatePermission('general', 'access_count_limitation', undefined);
                          } else {
                            const value = parseInt(e.target.value);
                            if (value && value > 0) {
                              updatePermission('general', 'access_count_limitation', value);
                            }
                          }
                        }}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        placeholder=""
                        min="1"
                        step="1"
                      />
                      <span className="text-sm text-gray-500">times</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum number of times this asset can be accessed
                  </p>
                </div>

                {/* Time-based Limitations */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Time-based Limitations
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          const newValue = !accessLimitationsEnabled;
                          setAccessLimitationsEnabled(newValue);
                          
                          // Update policy state
                          if (!newValue) {
                            // If disabling, clear time-based access limitations
                            setEditingPolicy(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings,
                                access_limitations: {
                                  ...prev.settings.access_limitations,
                                  allowed_access_from: undefined,
                                  allowed_access_to: undefined
                                }
                              }
                            }));
                          }
                          setHasUnsavedChanges(true);
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                          accessLimitationsEnabled ? 'bg-governate-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            accessLimitationsEnabled ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  {accessLimitationsEnabled && (
                    <div className="space-y-4">
                    {/* Radio buttons */}
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="timeOption"
                          value="days"
                          checked={timeLimitationType === 'days'}
                          onChange={() => {
                            handleTimeLimitationTypeChange('days');
                            setHasUnsavedChanges(true);
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Number of days from creation</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="timeOption"
                          value="datetime"
                          checked={timeLimitationType === 'datetime'}
                          onChange={() => {
                            handleTimeLimitationTypeChange('datetime');
                            setHasUnsavedChanges(true);
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Specific date/time range</span>
                      </label>
                    </div>

                    {/* Days input - only show when days is selected */}
                    {timeLimitationType === 'days' && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={daysValue}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDaysValue(value === '' ? '' : parseInt(value) || '');
                            setHasUnsavedChanges(true);
                          }}
                          className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                          min="1"
                          step="1"
                          placeholder=""
                        />
                        <select
                          value={daysUnit}
                          onChange={(e) => {
                            setDaysUnit(e.target.value as 'day' | 'week' | 'month' | 'year');
                            setHasUnsavedChanges(true);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        >
                          <option value="day">day(s)</option>
                          <option value="week">week(s)</option>
                          <option value="month">month(s)</option>
                          <option value="year">year(s)</option>
                        </select>
                        <span className="text-sm text-gray-500">from creation</span>
                      </div>
                    )}

                    {/* Date/Time Pickers - only show when datetime is selected */}
                    {timeLimitationType === 'datetime' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Access From
                          </label>
                          <div className="relative">
                            <DateTimePicker
                              value={datetimeFrom || null}
                              onChange={(value) => {
                                if (value) {
                                  setDatetimeFrom(value);
                                  updatePermission('general', 'allowed_access_from', value);
                                    const toValue = settings.access_limitations?.allowed_access_to;
                                  validateDatetimeValues(value, toValue);
                                } else {
                                  setDatetimeFrom('');
                                  updatePermission('general', 'allowed_access_from', undefined);
                                  setDatetimeErrors(prev => ({ ...prev, from: undefined }));
                                }
                              }}
                              showTimeSelect={true}
                              timeIntervals={1}
                              dateFormat="yyyy-MM-dd HH:mm"
                              returnFormat="iso"
                              placeholder="YYYY-MM-DD HH:MM"
                              className={datetimeErrors.from ? 'border-red-300 bg-red-50 dark:bg-red-950' : ''}
                              popperPlacement="bottom-end"
                            />
                          </div>
                          {datetimeErrors.from && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{datetimeErrors.from}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Access To
                          </label>
                          <div className="relative">
                            <DateTimePicker
                              value={datetimeTo || null}
                              onChange={(value) => {
                                if (value) {
                                  setDatetimeTo(value);
                                  updatePermission('general', 'allowed_access_to', value);
                                    const fromValue = settings.access_limitations?.allowed_access_from;
                                  validateDatetimeValues(fromValue, value);
                                } else {
                                  setDatetimeTo('');
                                  updatePermission('general', 'allowed_access_to', undefined);
                                  setDatetimeErrors(prev => ({ ...prev, to: undefined }));
                                }
                              }}
                              showTimeSelect={true}
                              timeIntervals={1}
                              dateFormat="yyyy-MM-dd HH:mm"
                              returnFormat="iso"
                              placeholder="YYYY-MM-DD HH:MM"
                              className={datetimeErrors.to ? 'border-red-300 bg-red-50 dark:bg-red-950' : ''}
                            />
                          </div>
                          {datetimeErrors.to && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{datetimeErrors.to}</p>
                          )}
                        </div>
                      </div>
                    )}
                    </div>
                  )}

                </div>
              </div>
          </div>

          {/* Recipient Message Section */}
          <div className="card">
            <div className="flex items-center space-x-2 mb-4">
              <DocumentIcon className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Recipient Message</h2>
            </div>
            
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Message for Recipients
              </label>
              <p className="text-sm text-gray-500">
                This message will be shown to recipients when they access files with this policy.
              </p>
              
              {isEditingRecipientMessage ? (
                <div className="space-y-2">
                  <textarea
                    value={recipientMessage}
                    onChange={(e) => setRecipientMessage(e.target.value)}
                    onBlur={() => {
                      updatePermission('general', 'recipient_message', recipientMessage);
                      setIsEditingRecipientMessage(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setRecipientMessage(settings.recipient_message || '');
                        setIsEditingRecipientMessage(false);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent resize-none"
                    rows={2}
                    placeholder="Enter a message for recipients..."
                    autoFocus
                  />
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        updatePermission('general', 'recipient_message', recipientMessage);
                        setIsEditingRecipientMessage(false);
                      }}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setRecipientMessage(settings.recipient_message || '');
                        setIsEditingRecipientMessage(false);
                      }}
                      className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setIsEditingRecipientMessage(true)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 focus:ring-2 focus:ring-governate-500 focus:border-transparent min-h-[2.5rem] flex items-center"
                >
                  {settings.recipient_message || (
                    <span className="text-gray-400">Click to add a message for recipients...</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Policy Recipients */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <UserGroupIcon className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Policy Recipients</h2>
              </div>
              <button
                onClick={() => setShowAddRecipientsModal(true)}
                className="btn-primary flex items-center space-x-2"
              >
                <PlusIcon className="w-4 h-4" />
                <span>Add Recipients</span>
              </button>
            </div>
            
            {recipientsLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-governate-500 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading recipients...</p>
              </div>
            ) : policyRecipients && policyRecipients.length > 0 ? (
              <div className="space-y-3">
                {policyRecipients.map((recipient, index) => {
                  if (recipient.user_group_id) {
                    const userGroup = userGroupsResponse?.groups?.find(g => g.id === recipient.user_group_id);
                    if (!userGroup) return null;
                    
                    const isExpanded = expandedGroups.has(userGroup.id);
                    
                    return (
                      <div key={`group-${index}`} className="border border-gray-200 rounded-lg">
                        <div className="flex items-center p-4">
                          <button
                            onClick={() => toggleGroupExpansion(userGroup.id)}
                            className="flex-1 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg p-2 -m-2"
                          >
                            <div className="flex items-center space-x-3">
                              <UserGroupIcon className="w-5 h-5 text-blue-600" />
                              <div className="text-left">
                                <p className="font-medium text-gray-900">{userGroup.name}</p>
                                <p className="text-sm text-gray-500">{userGroup.description}</p>
                              </div>
                            </div>
                            {isExpanded ? (
                              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={() => handleRemoveRecipient(recipient.id)}
                            disabled={removeRecipientMutation.isPending}
                            className="ml-2 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Remove recipient"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {isExpanded && (
                          <div className="border-t border-gray-200 bg-gray-50">
                            <div className="p-4">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">Group Members</h4>
                              {(() => {
                                const groupMembers = groupMembersData?.[userGroup.id] || [];
                                const memberContacts = contacts?.filter(contact => 
                                  groupMembers.some((member: any) => member.contact_id === contact.id)
                                ) || [];
                                
                                return memberContacts.length > 0 ? (
                                  <div className="space-y-2">
                                    {memberContacts.map((contact, memberIndex) => (
                                      <div key={memberIndex} className="flex items-center space-x-3 p-2 bg-white rounded-lg">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                          <span className="text-blue-600 font-medium text-sm">
                                            {contact.name ? contact.name.charAt(0).toUpperCase() : '?'}
                                          </span>
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-sm font-medium text-gray-900">{contact.name}</p>
                                          <p className="text-xs text-gray-500">{contact.email}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500">No members found</p>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  } else if (recipient.company_id) {
                    return (
                      <div key={`company-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <BuildingOfficeIcon className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="font-medium text-gray-900">Company</p>
                            <p className="text-sm text-gray-500">ID: {recipient.company_id}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveRecipient(recipient.id)}
                          disabled={removeRecipientMutation.isPending}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remove recipient"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  } else if (recipient.user_id) {
                    return (
                      <div key={`user-${index}`} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <UserIcon className="w-5 h-5 text-indigo-600" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{recipient.user_email || 'User'}</p>
                            {!recipient.user_email && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">ID: {recipient.user_id}</p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveRecipient(recipient.id)}
                          disabled={removeRecipientMutation.isPending}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remove recipient"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No recipients configured</h3>
                <p className="text-gray-500">This policy doesn't have any recipients assigned yet.</p>
              </div>
            )}
          </div>

          {/* Recipient Settings Section */}
          <div className="card">
            <div className="flex items-center space-x-2 mb-4">
              <UserGroupIcon className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Recipient Settings</h2>
            </div>
            
            {/* Allowed Domains */}
            <div className="space-y-3 mb-6">
              <h3 className="text-sm font-medium text-gray-700">Allowed Domains</h3>
              
              {/* Current Domains */}
              <div className="space-y-2">
                {(settings.recipient_settings?.allowed_domains || []).map((domain, index) => (
                  <div key={`domain-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">@</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{domain}</p>
                        <p className="text-xs text-gray-500">Domain</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeDomain(domain)}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {/* Show message if no domains */}
                {(settings.recipient_settings?.allowed_domains?.length || 0) === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    <p className="text-sm">No domains configured (allows any domain)</p>
                  </div>
                )}
              </div>

              {/* Add New Domain */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add New Domain
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'domain')}
                    placeholder="Enter domain (e.g., company.com)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  />
                  <button
                    onClick={addDomain}
                    disabled={!newDomain.trim() || isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Enter a domain name. When empty, any domain is allowed.
                </p>
              </div>
            </div>

            {/* Allowed Subdomains */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Allowed Subdomains</h3>
              
              {/* Current Subdomains */}
              <div className="space-y-2">
                {(settings.recipient_settings?.allowed_subdomains || []).map((subdomain, index) => (
                  <div key={`subdomain-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">@</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{subdomain}</p>
                        <p className="text-xs text-gray-500">Subdomain</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeSubdomain(subdomain)}
                      className="p-1 text-red-500 hover:text-red-700 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                {/* Show message if no subdomains */}
                {(settings.recipient_settings?.allowed_subdomains?.length || 0) === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    <p className="text-sm">No subdomains configured</p>
                  </div>
                )}
              </div>

              {/* Add New Subdomain */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add New Subdomain
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newSubdomain}
                    onChange={(e) => setNewSubdomain(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'subdomain')}
                    placeholder="Enter subdomain (e.g., subdomain.company.com)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  />
                  <button
                    onClick={addSubdomain}
                    disabled={!newSubdomain.trim() || isSubmitting}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Enter a subdomain name.
                </p>
              </div>
            </div>
          </div>

          {/* Tags Section */}
          {policyData && (
            <div className="card">
              <div className="flex items-center space-x-2 mb-4">
                <TagIcon className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tags</h2>
              </div>
              <TagManager
                resourceType="policy"
                resourceId={policyData.id}
                onTagsChange={() => {
                  // Optionally refresh policy data
                  queryClient.invalidateQueries({ queryKey: ['policy', id] });
                }}
              />
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* File Permissions - Only show for file assets */}
          {editingPolicy.asset_type === 'file' && (
            <div className="card">
              <div className="flex items-center space-x-2 mb-4">
                <DocumentIcon className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">File Permissions</h2>
              </div>
              
              <div className="space-y-4">
                {[
                  { key: 'convert_original', label: 'Convert to original', icon: ArrowPathIcon },
                  { key: 'save_changes', label: 'Save changes to secured asset', icon: PencilIcon },
                  { key: 'copy_paste', label: 'Copy/Paste', icon: ClipboardDocumentIcon },
                  { key: 'screen_capture', label: 'Screenshot', icon: CameraIcon },
                  { key: 'screen_watermark', label: 'Screen watermark', icon: EyeIcon },
                  { key: 'print', label: 'Print', icon: PrinterIcon },
                  { key: 'print_watermark', label: 'Print watermark', icon: PrinterIcon },
                  { key: 'share', label: 'Share', icon: UserGroupIcon },
                ]
                .filter(({ key }) => {
                  // Filter based on company policy settings, using policy's created_at for apply_after logic
                  const createdAt = policyData?.created_at;
                  if (key === 'screen_watermark' || key === 'print_watermark') {
                    return isFilePermissionVisible(key, createdAt);
                  }
                  return isFilePermissionVisible(key, createdAt);
                })
                .map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Icon className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                    </div>
                    <ToggleSwitch
                      checked={key === 'screen_watermark'
                        ? settings.screen_watermark
                        : key === 'print_watermark'
                        ? settings.print_watermark
                        : settings.file_permissions?.[key as keyof FilePermissions]?.allowed || false}
                      onChange={(checked) => {
                        if (key === 'screen_watermark') {
                          updatePermission('watermark', 'screen_enabled', checked);
                        } else if (key === 'print_watermark') {
                          updatePermission('watermark', 'print_enabled', checked);
                        } else {
                          updatePermission('file_permissions', key, checked);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Folder Permissions - Only show for folder assets */}
          {editingPolicy.asset_type === 'folder' && (
            <div className="card">
              <div className="flex items-center space-x-2 mb-4">
                <FolderIcon className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Folder Permissions</h2>
              </div>
              
              <div className="space-y-4">
                {[
                  { key: 'create', label: 'Create', icon: FolderPlusIcon },
                  { key: 'copy', label: 'Copy', icon: DocumentDuplicateIcon },
                  { key: 'add', label: 'Add', icon: PlusCircleIcon },
                  { key: 'move', label: 'Move', icon: ArrowRightCircleIcon },
                  { key: 'share', label: 'Share', icon: UserGroupIcon },
                  { key: 'delete', label: 'Delete', icon: TrashIcon },
                ]
                .filter(({ key }) => {
                  // Filter based on company policy settings, using policy's created_at for apply_after logic
                  const createdAt = policyData?.created_at;
                  return isFolderPermissionVisible(key, createdAt);
                })
                .map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Icon className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">{label}</span>
                    </div>
                    <ToggleSwitch
                      checked={settings.folder_permissions?.[key as keyof ApiFolderPermissions]?.allowed || false}
                      onChange={(checked) => updatePermission('folder_permissions', key, checked)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Add Recipients - Direct Component */}
      {showAddRecipientsModal && (
        <PolicyRecipientSelector
          selectedRecipients={[]}
          onRecipientsChange={handleAddRecipients}
          disabled={addRecipientsMutation.isPending}
          autoOpenAddModal={true}
          onClose={() => setShowAddRecipientsModal(false)} // 🎯 NEW: Reset modal state
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <TrashIcon className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Remove Recipient</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-700 mb-6">
                Are you sure you want to remove this recipient from the policy? This will revoke their access to the policy.
              </p>
              <div className="flex space-x-3 justify-end">
                <button
                  onClick={cancelDeleteRecipient}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteRecipient}
                  disabled={removeRecipientMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {removeRecipientMutation.isPending ? 'Removing...' : 'Remove Recipient'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyEdit;
